import { useQuery } from "@tanstack/react-query";
import { googleContactKeys } from "@/lib/query";

export interface GoogleContact {
  name: string;
  email: string;
}

/**
 * Server-side search against Google Contacts (People API) for the
 * calendar-invite recipient autocomplete — see GoogleRecipientInput. Gated
 * to 2+ characters to avoid firing a search on every keystroke. Never
 * throws: the server degrades an unreachable/unconnected Google account to
 * an empty list rather than an error, since autocomplete failing must never
 * block manually typing a literal recipient (EPIC-013's "Manually Enter
 * Recipient Emails" story).
 */
export function useGoogleContactsSearch(search: string) {
  const trimmed = search.trim();

  return useQuery({
    queryKey: googleContactKeys.search(trimmed),
    queryFn: async (): Promise<GoogleContact[]> => {
      const res = await fetch(`/api/google/contacts?search=${encodeURIComponent(trimmed)}`);
      if (!res.ok) return [];
      const json = await res.json();
      return json.data;
    },
    enabled: trimmed.length >= 2,
  });
}
